import dayjs from 'dayjs';
import type { FC } from 'react';
import { useCallback, useRef, useState, useEffect } from 'react';
import { Button } from '../../components/button';
import { Icon } from '../../icon-svg/pc-icon';
import { GlobalImages } from '../../images';
import { useOpenModal } from '../../next-modal';
import { DATE_FORMAT } from '../const';
import { RenderMarkdown } from './render-markdown';
import html2canvas from '@flowus/html2canvas';
import { sleep } from '../../async';
import { message } from '../../components/message';
import { useChatTranslation } from '../hooks/use-i18n';
import * as clipboard from 'clipboard-polyfill';
import { isMobile } from 'react-device-detect';
import { downloadImage } from '../hooks/utils';
import { LoadingIcon } from '../../components';
const modalId = 'share-modal';
interface ShareProps {
  title: string;
  createdAt: number;
  md: string;
}

const Share: FC<ShareProps> = (props) => {
  const openModal = useOpenModal();
  const { title, createdAt, md } = props;
  const contentRef = useRef<HTMLDivElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const { t } = useChatTranslation();
  const [imageLoading, setImageLoading] = useState(true);

  const generateCanvas = async () => {
    if (!contentRef.current) return null;
    try {
      return await html2canvas(contentRef.current, {
        scale: 5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
      });
    } catch (error) {
      // console.error('Failed to generate canvas:', error);
      return null;
    }
  };

  const generateImage = async () => {
    const canvas = await generateCanvas();
    if (canvas) {
      setPreviewUrl(canvas.toDataURL('image/png'));
      setImageLoading(false);
    }
  };

  useEffect(() => {
    void sleep(1000).then(() => {
      void generateImage();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopyImage = async () => {
    if (!previewUrl) return;

    try {
      const response = await fetch(previewUrl);
      const blob = await response.blob();
      const clipboardItem = new ClipboardItem({ 'image/png': blob });
      await navigator.clipboard.write([clipboardItem]);
      message.success(t('chatCopySuccess'));
    } catch (error) {
      // console.error('Failed to copy image:', error);
    }
  };

  const handleDownloadImage = async () => {
    if (!previewUrl) return;

    try {
      downloadImage(previewUrl, `${title || 'share'}-${dayjs(createdAt).format('YYYY-MM-DD')}.png`);
      message.success(t('chatDownloadSuccess'));
    } catch (error) {
      // console.error('Failed to download image:', error);
    }
  };

  return (
    <div className="flex flex-col max-w-full w-[600px] pb-6">
      <div className="text-h3 font-medium px-4 py-3 flex justify-between items-center">
        <span>{t('chatShareTitle')}</span>
        <Icon
          name="IcClose"
          className="text-grey3"
          size="middle"
          onClick={() => openModal.closeModal(modalId)}
        />
      </div>
      <div className="mx-4 mb-4 overflow-auto max-h-120 h-full border rounded-md">
        <div
          hidden={!!previewUrl}
          ref={contentRef}
          className={'relative p-8 w-full bg-white2 font-sans'}
        >
          {imageLoading && (
            <div className="fixed w-full h-full flex items-center justify-center left-0">
              <LoadingIcon />
            </div>
          )}
          <img
            src={GlobalImages.logoWithText}
            crossOrigin="anonymous"
            referrerPolicy="no-referrer"
            className="h-8"
            alt=""
          />
          <div className="h-6" />
          <div className="space-y-1">
            <h1 className="text-h2 font-medium">{title}</h1>
            <div className="text-t2 text-grey3 space-x-2">
              <span>{dayjs(createdAt).format(DATE_FORMAT)}</span>
              <span>{t('chatGeneratedByAI')}</span>
            </div>
          </div>
          <div className="h-6" />
          <div className="w-full h-px bg-grey6" />
          <div className="h-6" />
          <RenderMarkdown showToc={false} markdown={md} isShare={true} />
        </div>
        {previewUrl && (
          <img hidden={!previewUrl} src={previewUrl} alt="Preview" className="w-full" />
        )}
      </div>
      <div className="w-full px-6 grid grid-cols-2 gap-4 shrink-0">
        <Button className="w-full" size="middle" onClick={() => openModal.closeModal(modalId)}>
          {t('chatShareCancel')}
        </Button>
        <Button
          className="w-full"
          size="middle"
          colorType="active"
          onClick={isMobile ? handleDownloadImage : handleCopyImage}
          disable={!previewUrl}
        >
          {isMobile ? t('chatShareDownloadImage') : t('chatShareCopyImage')}
        </Button>
      </div>
    </div>
  );
};

export const useOpenShare = () => {
  const openModal = useOpenModal();
  return useCallback(
    (props: ShareProps) => {
      openModal.modal({
        modalId,
        className: 'overflow-hidden',
        center: !isMobile,
        content: () => <Share {...props} />,
      });
    },
    [openModal]
  );
};
